import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Menu, X } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

const navLinks = [
  { label: 'Home', to: '/' },
  {
    label: 'Company',
    children: [
      { label: 'About Us', to: '/about-us' },
      { label: 'Contact Us', to: '/contact-us' },
      { label: 'FAQ', to: '/faq' },
    ],
  },
  {
    label: 'Services',
    children: [
      { label: 'Software Development', to: '/services/software-development' },
      { label: 'SEO', to: '/services/seo' },
      { label: 'Paid Advertising', to: '/services/paid-advertisement' },
      { label: 'Social Media', to: '/services/social-media' },
      { label: 'Content Marketing', to: '/services/content-marketing' },
      { label: 'Payment Links', to: '/payment-link-checkout' },
    ],
  },
  { label: 'Pricing', to: '/pricing' },
  {
    label: 'Legal',
    children: [
      { label: 'Privacy Policy', to: '/privacy-policy' },
      { label: 'Terms & Conditions', to: '/terms-conditions' },
      { label: 'Refund Policy', to: '/return-refund-policy' },
    ],
  },
  { label: 'Contact', to: '/contact-us' },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [openMobileSub, setOpenMobileSub] = useState(null)
  const location = useLocation()
  const { isAuthenticated } = useAuth()
  const navRef = useRef(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setOpenDropdown(null)
  }, [location])

  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <>
      <style>{css}</style>
      <header className={`nb-header ${scrolled ? 'nb-scrolled' : ''}`} ref={navRef}>
        <div className="nb-container">
          <Link to="/" className="nb-brand">
            <span className="nb-logo">
              <span className="nb-logo-a">A</span><span className="nb-logo-1">1</span><span className="nb-logo-x">X</span><span className="nb-logo-pay">PAY</span>
            </span>
          </Link>

          <nav className="nb-nav-desktop">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="nb-dropdown-wrap"
                  onMouseEnter={() => setOpenDropdown(link.label)}
                  onMouseLeave={() => setOpenDropdown(null)}>
                  <button className={`nb-link nb-link-drop ${openDropdown === link.label ? 'nb-link-active' : ''}`}>
                    {link.label}
                    <ChevronDown size={13} className={`nb-chevron ${openDropdown === link.label ? 'nb-chevron-open' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openDropdown === link.label && (
                      <motion.div className="nb-dropdown"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.16 }}>
                        {link.children.map((c) => (
                          <Link key={c.label} to={c.to} className="nb-drop-item">{c.label}</Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link key={link.label} to={link.to}
                  className={`nb-link ${location.pathname === link.to ? 'nb-link-current' : ''}`}>
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="nb-cta-group">
            {isAuthenticated() ? (
              <Link to="/user/dashboard" className="nb-btn-primary">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="nb-btn-ghost">Sign in</Link>
                <Link to="/register" className="nb-btn-primary">Open Account</Link>
              </>
            )}
          </div>

          <button className="nb-hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div className="nb-mobile-menu"
              initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label} className="nb-mobile-group">
                    <button className="nb-mobile-link nb-mobile-parent"
                      onClick={() => setOpenMobileSub(openMobileSub === link.label ? null : link.label)}>
                      {link.label}
                      <ChevronDown size={13} className={openMobileSub === link.label ? 'nb-chevron-open' : ''} />
                    </button>
                    <AnimatePresence>
                      {openMobileSub === link.label && (
                        <motion.div className="nb-mobile-sub"
                          initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }}>
                          {link.children.map((c) => <Link key={c.label} to={c.to} className="nb-mobile-child">{c.label}</Link>)}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link key={link.label} to={link.to} className="nb-mobile-link">{link.label}</Link>
                )
              )}
              <div className="nb-mobile-ctas">
                {isAuthenticated() ? (
                  <Link to="/user/dashboard" className="nb-btn-primary nb-btn-full">Dashboard</Link>
                ) : (
                  <>
                    <Link to="/login" className="nb-btn-ghost nb-btn-full">Sign in</Link>
                    <Link to="/register" className="nb-btn-primary nb-btn-full">Open Account</Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;800;900&display=swap');
.nb-header {
  position: sticky; top: 0; z-index: 1000; width: 100%;
  background: #fff; box-shadow: 0 1px 0 rgba(0,0,0,0.07);
  transition: box-shadow 0.3s; font-family: 'Jost', sans-serif;
}
.nb-header.nb-scrolled { box-shadow: 0 4px 20px rgba(10,16,35,0.10); }
.nb-container {
  max-width: 1200px; margin: 0 auto; padding: 0 1.5rem;
  display: flex; align-items: center; gap: 1.5rem; height: 70px;
}
@media (min-width:1200px) { .nb-container { padding: 0 2.5rem; } }
.nb-brand { display: flex; align-items: center; text-decoration: none; flex-shrink: 0; }
.nb-logo {
  display: inline-flex; align-items: baseline;
  font-size: 1.5rem; font-weight: 900; letter-spacing: -0.04em; line-height: 1;
  font-family: 'Jost', sans-serif;
}
.nb-logo-a {
  background: linear-gradient(170deg, #4A5568 0%, #1A202C 40%, #4A5568 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  filter: drop-shadow(0 1px 0 rgba(255,255,255,0.5));
}
.nb-logo-1 {
  background: linear-gradient(170deg, #FCD34D 0%, #F59E0B 50%, #B45309 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  filter: drop-shadow(0 1px 0 rgba(255,255,255,0.4));
}
.nb-logo-x {
  background: linear-gradient(170deg, #4A5568 0%, #1A202C 40%, #4A5568 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  filter: drop-shadow(0 1px 0 rgba(255,255,255,0.5));
}
.nb-logo-pay {
  background: linear-gradient(135deg, #1A4DBE 0%, #3B82F6 60%, #06B6D4 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.nb-nav-desktop { display: none; flex: 1; align-items: center; gap: 0.125rem; }
@media (min-width:1024px) { .nb-nav-desktop { display: flex; } }
.nb-link {
  padding: 0.45rem 0.75rem; font-size: 0.9375rem; font-weight: 500;
  color: #2C3655; text-decoration: none; border-radius: 0.5rem;
  transition: color 0.15s, background 0.15s; white-space: nowrap;
  background: none; border: none; cursor: pointer; font-family: 'Jost', sans-serif;
  display: flex; align-items: center; gap: 0.25rem;
}
.nb-link:hover, .nb-link-active { color: #1A4DBE; background: #EEF3FF; }
.nb-link-current { color: #1A4DBE; font-weight: 600; }
.nb-chevron { transition: transform 0.2s; flex-shrink: 0; }
.nb-chevron-open { transform: rotate(180deg); }
.nb-dropdown-wrap { position: relative; }
.nb-dropdown {
  position: absolute; top: calc(100% + 4px); left: 0; min-width: 200px;
  background: #fff; border: 1px solid #E8EBF4;
  border-radius: 0.75rem; padding: 0.375rem;
  box-shadow: 0 8px 28px rgba(26,77,190,0.13); z-index: 100;
}
.nb-drop-item {
  display: block; padding: 0.5rem 0.875rem; font-size: 0.875rem; font-weight: 500;
  color: #2C3655; text-decoration: none; border-radius: 0.5rem; transition: all 0.15s;
}
.nb-drop-item:hover { background: #EEF3FF; color: #1A4DBE; }
.nb-cta-group { display: none; align-items: center; gap: 0.5rem; margin-left: auto; }
@media (min-width:1024px) { .nb-cta-group { display: flex; } }
.nb-btn-primary {
  display: inline-flex; align-items: center; padding: 0.5rem 1.375rem;
  font-size: 0.9375rem; font-weight: 600; color: #fff; background: #1A4DBE;
  border-radius: 50px; text-decoration: none; border: 1.5px solid #1A4DBE;
  transition: background 0.2s, transform 0.15s; font-family: 'Jost', sans-serif; white-space: nowrap;
}
.nb-btn-primary:hover { background: #1540a8; transform: translateY(-1px); }
.nb-btn-ghost {
  display: inline-flex; align-items: center; padding: 0.5rem 1.25rem;
  font-size: 0.9375rem; font-weight: 600; color: #0A1023;
  background: transparent; border: 1.5px solid #D0D5E8; border-radius: 50px;
  text-decoration: none; transition: border-color 0.2s, color 0.2s;
  font-family: 'Jost', sans-serif; white-space: nowrap;
}
.nb-btn-ghost:hover { border-color: #1A4DBE; color: #1A4DBE; }
.nb-hamburger {
  display: flex; align-items: center; justify-content: center;
  width: 2.25rem; height: 2.25rem; background: none; border: 1.5px solid #E8EBF4;
  border-radius: 0.5rem; cursor: pointer; color: #0A1023; margin-left: auto;
  transition: border-color 0.15s;
}
.nb-hamburger:hover { border-color: #1A4DBE; color: #1A4DBE; }
@media (min-width:1024px) { .nb-hamburger { display: none; } }
.nb-mobile-menu {
  background: #fff; border-top: 1px solid #E8EBF4; overflow: hidden;
  padding: 0.5rem 1.5rem 1.25rem;
}
.nb-mobile-link {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.7rem 0; font-size: 0.9375rem; font-weight: 500; color: #2C3655;
  text-decoration: none; border-bottom: 1px solid #F1F3F9;
  background: none; border-left: none; border-right: none; border-top: none;
  width: 100%; cursor: pointer; font-family: 'Jost', sans-serif; text-align: left;
  transition: color 0.15s;
}
.nb-mobile-link:hover { color: #1A4DBE; }
.nb-mobile-sub { overflow: hidden; }
.nb-mobile-child {
  display: block; padding: 0.45rem 0 0.45rem 1rem;
  font-size: 0.875rem; color: #4a4a6a; text-decoration: none; transition: color 0.15s;
}
.nb-mobile-child:hover { color: #1A4DBE; }
.nb-mobile-ctas { display: flex; flex-direction: column; gap: 0.625rem; margin-top: 1rem; }
.nb-btn-full { justify-content: center; width: 100%; }
`
